/**
 * Maintenance: stale player cleanup.
 */

import {
  ref, get, update, remove, push,
  serverTimestamp,
} from "firebase/database";
import { getDb, getServerTime, votesToArray, arrayToVotes, type RoomData, type PlayerData } from "./helpers";
import { removeFromPublicRoomIndex } from "./public-rooms";
import { STALE_CONNECTED_TIMEOUT_MS } from "@/shared/constants";
import { disconnectedDurationMs, isEffectivelyConnected } from "@/shared/presence";

export async function pruneStalePlayers(
  roomCode: string,
  requesterId: string,
  graceMs: number,
  removalMs?: number
): Promise<string[]> {
  const db = getDb();
  const roomRef = ref(db, `rooms/${roomCode}`);
  const playersRef = ref(db, `rooms/${roomCode}/players`);

  const [roomSnap, playersSnap] = await Promise.all([get(roomRef), get(playersRef)]);
  if (!roomSnap.exists()) throw new Error("Room not found");

  const roomData = roomSnap.val() as RoomData;
  if (roomData.ownerId !== requesterId) throw new Error("Not room owner");

  const players = (playersSnap.val() || {}) as Record<string, PlayerData>;
  // Use server-adjusted time to prevent stale player checks from being skewed by client clocks
  const now = await getServerTime();

  // Tier-1: players effectively disconnected >= graceMs — demote (clear team/role)
  // Tier-2: players effectively disconnected >= removalMs — fully remove from room
  // "Effectively disconnected" includes:
  // - connected !== true, OR
  // - connected === true with stale heartbeat (lastSeen too old)
  const toRemove: { id: string; name: string }[] = [];   // tier-2
  const toDemote: { id: string; name: string }[] = [];   // tier-1 only (not yet at removal threshold)

  Object.entries(players).forEach(([id, p]) => {
    if (isEffectivelyConnected(p, now, STALE_CONNECTED_TIMEOUT_MS)) return;
    const disconnectedFor = disconnectedDurationMs(p, now);

    if (removalMs !== undefined && disconnectedFor >= removalMs) {
      toRemove.push({ id, name: p.name });
    } else if (disconnectedFor >= graceMs) {
      // Only demote if they still have a team/role to clear
      if (p.team != null || p.role != null) {
        toDemote.push({ id, name: p.name });
      }
    }
  });

  // --- Tier-1: demote ---
  if (toDemote.length > 0) {
    const updates: Record<string, unknown> = {};

    toDemote.forEach(({ id }) => {
      const player = players[id];
      if (!player) return;
      if (player.connected === true) updates[`players/${id}/connected`] = false;
      if (player.team != null) updates[`players/${id}/team`] = null;
      if (player.role != null) updates[`players/${id}/role`] = null;
    });

    // Remove votes of demoted players from board
    const demoteIds = new Set(toDemote.map((p) => p.id));
    const board = roomData.board || [];
    let boardChanged = false;
    const updatedBoard = board.map((card) => {
      if (!card.votes) return card;
      const votes = votesToArray(card.votes);
      const filteredVotes = votes.filter((id) => !demoteIds.has(id));
      if (filteredVotes.length === votes.length) return card;
      boardChanged = true;
      return { ...card, votes: arrayToVotes(filteredVotes) };
    });
    if (boardChanged) updates.board = updatedBoard;

    if (Object.keys(updates).length > 0) await update(roomRef, updates);

    const names = toDemote.map((p) => p.name).join(", ");
    await push(ref(db, `rooms/${roomCode}/messages`), {
      playerId: null,
      playerName: "System",
      message: `${names} moved to spectators (disconnected)`,
      timestamp: serverTimestamp(),
      type: "system",
    });
  }

  // --- Tier-2: fully remove long-gone players ---
  if (toRemove.length > 0) {
    await Promise.all(
      toRemove.map(({ id }) => remove(ref(db, `rooms/${roomCode}/players/${id}`)))
    );

    // Check if room is now completely empty (no connected players and no remaining entries)
    const updatedPlayersSnap = await get(playersRef);
    const remaining = (updatedPlayersSnap.val() || {}) as Record<string, PlayerData>;
    const anyoneLeft = Object.values(remaining).some((p) => p.connected !== false);

    if (!anyoneLeft && Object.keys(remaining).length === 0) {
      // Room is empty — delete it entirely
      await Promise.all([
        remove(roomRef),
        removeFromPublicRoomIndex(roomCode),
      ]);
      // Room deleted — nothing more to do
      return [...toDemote, ...toRemove].map((p) => p.id);
    }
  }

  return [...toDemote, ...toRemove].map((p) => p.id);
}
